import { CatList, CatSearchForm } from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import EquipmentListItem from './equipment-search-items-list';
import styles from './request-equipment-service-search.module.scss';
import { useCallback, useMemo } from 'react';
import { getFilteredList } from 'services/equipment/utils';
import { MY_EQUIPMENT_SEARCH_KEYS } from 'services/equipment/constants';
import {
  Conditional,
  useBreakpoint,
  VirtualScroll
} from 'components/pcc-components';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { useController, useFormContext } from 'react-hook-form';
import clx from 'classnames';
import EquipmentSearchNoResults from './equipment-search-no-results';
import { REQUEST_SERVICE_EQUIPMENT_SEARCH, SEARCH_NAME } from '../constant';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { getFormeventPayload, getHeight } from '../utils';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { RequestEquipmentServiceSearchProps } from '../types';
import { useRequestEquipmentService } from '../request-equipment-service-context';

const RequestEquipmentServiceSearch = ({
  equipmentList,
  placeholder,
  hasNotSelectedEquipment
}: RequestEquipmentServiceSearchProps) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const { setEquipmentInfo } = useRequestEquipmentService();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_SM);
  const name = 'equipment';
  const { setValue, watch, clearErrors, getValues } = useFormContext();
  const formData = getValues();
  const { fieldState } = useController({
    name,
    rules: {
      required: {
        value: hasNotSelectedEquipment,
        message: t('PLEASE_SELECT_AN_EQUIPMENT')
      }
    }
  });
  const status = fieldState?.error ? 'error' : undefined;
  const errorNote = fieldState?.error?.message;
  const searchValue = watch(SEARCH_NAME) ?? '';

  const equipments = useMemo(() => {
    if (searchValue) {
      const filteredList = getFilteredList(
        equipmentList,
        searchValue,
        MY_EQUIPMENT_SEARCH_KEYS
      );
      return filteredList;
    }
    return [];
  }, [equipmentList, searchValue]);
  const containerHeight = isMD ? getHeight(equipments) : `calc(100vh - 75px)`;
  const handleEquipmentSearch = (searchTerm: string) => {
    setValue(SEARCH_NAME, searchTerm);
  };
  const showEquipment = !isEmpty(equipments) && !isEmpty(searchValue);
  const noResults = isEmpty(equipments) && searchValue.length > 0;

  const onEquipmentSelect = useCallback(
    (item) => {
      const { serialNumber, model, equipmentFamily } = item;
      const formAdditionalContent =
        serialNumber ?? `${model} ${equipmentFamily}`;
      setEquipmentInfo(item);
      clearErrors(name);
      setValue(SEARCH_NAME, '');
      getFormeventPayload(
        REQUEST_SERVICE_EQUIPMENT_SEARCH,
        formData,
        true,
        fireEvent,
        formAdditionalContent,
        searchValue,
        ''
      );
    },
    [clearErrors, fireEvent, formData, searchValue, setEquipmentInfo, setValue]
  );

  const renderListItem = useCallback(
    (item) => {
      return (
        <EquipmentListItem
          item={item}
          searchTerm={searchValue}
          onEquipmentSelect={onEquipmentSelect}
        />
      );
    },
    [onEquipmentSelect, searchValue]
  );

  return (
    <div
      className={clx('mx-3 pt-3 pb-3 mb-2', {
        'request-service-error': errorNote
      })}
    >
      <CatSearchForm
        status={status}
        data-testid="cat-header-search-bar"
        value={searchValue}
        errorNote={errorNote}
        placeholder={placeholder}
        name={SEARCH_NAME}
        size="md"
        onBlChange={(e) => {
          handleEquipmentSearch(e.detail.value ?? '');
        }}
        className={styles['search-list-height']}
      >
        <Conditional test={showEquipment}>
          <div className={styles['search-list-container']}>
            <CatList>
              <VirtualScroll
                containerHeight={containerHeight}
                items={equipments}
                itemHeight={80}
                renderItem={renderListItem}
              />
            </CatList>
          </div>
        </Conditional>
        <Conditional test={noResults}>
          <EquipmentSearchNoResults />
        </Conditional>
      </CatSearchForm>
    </div>
  );
};
export default RequestEquipmentServiceSearch;
